import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Users, GraduationCap, Receipt, FileText, Image as ImageIcon, TrendingUp, TrendingDown, Minus, CheckCircle2, AlertTriangle, RefreshCw, Activity, Clock, Bell, Copy, ExternalLink, Check, ArrowRight, Calendar } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import InstallAppButton from "../../components/InstallAppButton";

export default function Dashboard() {
  const { user: me, can } = useAuth();
  const isOwner = me?.role === "owner";
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [outstanding, setOutstanding] = useState([]);
  // `termFilter === ""` means "site current term" — the server resolves
  // that to `settings.current_term`. Any other value is passed through as
  // ?term=<label> so revenue/pending/active-students refetch scoped to it.
  const [termFilter, setTermFilter] = useState("");
  // `location.key` changes on every navigation (even to the same URL), so
  // adding it to the dep array re-fetches stats whenever the admin returns
  // to the dashboard — keeps totals fresh after an invoice
  // delete / mark-paid / edit elsewhere.
  useEffect(() => {
    const params = termFilter ? { term: termFilter } : {};
    api.get("/admin/stats", { params }).then((r) => setStats(r.data)).catch(() => {});
  }, [location.key, termFilter]);
  // Outstanding payments expose £ owed — owner only.
  useEffect(() => {
    if (!isOwner) return;
    api.get("/admin/invoices/outstanding").then((r) => setOutstanding(r.data || [])).catch(() => {});
  }, [isOwner, location.key]);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get("/admin/debug/health");
      setHealth(data);
    } catch (e) {
      setHealth({ ok: false, error: e?.response?.data?.detail || "Failed to run health check" });
    } finally { setHealthLoading(false); }
  }, []);
  // Health card surfaces internal infra hints — owner only.
  useEffect(() => { if (isOwner) loadHealth(); }, [loadHealth, isOwner]);

  // Cards are gated by section permission (owner has everything).
  // £-denominated cards are additionally owner-only — staff who happen to
  // have the `invoices` perm see invoice counts but not revenue/pending £.
  const allCards = [
    { perm: "leads",    label: "Total leads",                                       value: stats?.leads_total,      icon: Users,         color: "#E8C37A" },
    { perm: "leads",    label: "Hot / warm leads",                                  value: stats?.leads_hot_warm,    icon: TrendingUp,    color: "#A685BD" },
    { perm: "students", label: `Active students (${stats?.focus_term || stats?.current_term || "current"})`, value: stats?.active_students, icon: GraduationCap, color: "#8EC9C7" },
    { perm: "students", label: "All students (lifetime)",                            value: stats?.students_total,    icon: GraduationCap, color: "#A685BD" },
    { perm: "invoices", label: "Revenue (paid)",                                     value: `£${(stats?.revenue_gbp || 0).toFixed(0)}`,         icon: Receipt,       color: "#5D2C8F", ownerOnly: true },
    { perm: "invoices", label: "Pending payments",                                   value: `£${(stats?.pending_revenue_gbp || 0).toFixed(0)}`, icon: Receipt,       color: "#E8C37A", ownerOnly: true },
    { perm: "invoices", label: "Invoices pending",                                   value: stats?.invoices_pending,  icon: Receipt,       color: "#E8C37A" },
    { perm: "blog",     label: "Blog posts",                                         value: `${stats?.blog_published || 0} / ${stats?.blog_posts || 0}`, icon: FileText, color: "#A685BD" },
    { perm: "social",   label: "Class photos",                                       value: stats?.photos,            icon: ImageIcon,     color: "#8EC9C7" },
  ];
  const cards = allCards.filter((c) => (c.ownerOnly ? isOwner : true) && can(c.perm));

  // Quick links are filtered by section permission so staff don't see
  // dead links to pages they'll only get 403s from.
  const allQuickLinks = [
    { perm: "leads",        to: "/admin/leads",    label: "Add or review leads" },
    { perm: "students",     to: "/admin/students", label: "Enrol a new student" },
    { perm: "invoices",     to: "/admin/invoices", label: "Create or mark invoice paid" },
    { perm: "blog",         to: "/admin/blog",     label: "Generate this week's blog post" },
    { perm: "social",       to: "/admin/social",   label: "Upload photos → social captions" },
    { perm: "welcome_pack", to: "/admin/welcome",  label: "Edit the welcome letter" },
  ];
  const quickLinks = allQuickLinks.filter((q) => can(q.perm));

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">welcome back</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Dashboard</h1>
        </div>
        {/* Term filter — filters revenue / pending / active-student counters
            to a specific term. Server defaults to current_term when unset. */}
        {Array.isArray(stats?.available_terms) && stats.available_terms.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-[#6E6074]" data-testid="dashboard-term-filter-label">
            <Calendar size={14} className="text-[#5D2C8F]" />
            <span className="uppercase tracking-widest text-[10px]">Term</span>
            <select
              value={termFilter}
              onChange={(e) => setTermFilter(e.target.value)}
              className="bg-white border border-[#E5E0D8] rounded px-3 py-1.5 text-sm text-[#5D2C8F]"
              data-testid="dashboard-term-filter"
            >
              <option value="">Current ({stats?.current_term})</option>
              {stats.available_terms
                .filter((t) => t.label && t.label !== stats?.current_term)
                .map((t) => (
                  <option key={t.label} value={t.label}>{t.label}</option>
                ))}
            </select>
          </label>
        )}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="admin-surface p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#6E6074] uppercase tracking-widest">{c.label}</span>
              <c.icon size={18} style={{ color: c.color }} />
            </div>
            <div className="font-display text-3xl text-[#5D2C8F] mt-2">{c.value ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* --- Term breakdown --- visible to any admin with students OR invoices access.
           For staff this is the ONLY way to see "who's enrolled for Autumn" at a
           glance without needing revenue figures. Rows link to the students/
           invoices pages pre-filtered to that term. */}
      {(can("students") || can("invoices")) && Array.isArray(stats?.terms_breakdown) && stats.terms_breakdown.length > 0 && (
        <div className="admin-surface p-6 mt-6" data-testid="terms-breakdown-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl text-[#5D2C8F] flex items-center gap-2"><Calendar size={16} /> By term</h3>
            <span className="text-xs text-[#6E6074]">Previous · current · upcoming</span>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {stats.terms_breakdown.map((t) => (
              <TermBreakdownRow key={t.label} term={t} can={can} />
            ))}
          </div>
        </div>
      )}

      {/* --- System health --- owner only (shows internal env-var hints) */}
      {isOwner && <HealthCard health={health} loading={healthLoading} onRefresh={loadHealth} />}

      {/* --- Term revenue comparison chip --- owner only (£ totals) */}
      {isOwner && <TermRevenueChip stats={stats} />}

      {/* --- Outstanding payments --- owner only (£ balances) */}
      {isOwner && <OutstandingCard rows={outstanding} />}

      {/* --- UptimeRobot setup (one-click setup guide) --- owner only (config) */}
      {isOwner && <UptimeRobotCard />}

      {/* --- Install-as-app prompt (auto-hides if already installed or not supported) --- */}
      <InstallAppButton />

      {quickLinks.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-5 mt-8">
          <div className="admin-surface p-6 lg:col-span-2">
            <h3 className="font-display text-xl text-[#5D2C8F]">Quick actions</h3>
            <p className="text-sm text-[#6E6074] mt-1">Common things you do on a Tuesday morning.</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {quickLinks.map((q) => (
                <QuickLink key={q.to} to={q.to} label={q.label} />
              ))}
            </div>
          </div>
          <div className="admin-surface p-6">
            <h3 className="font-display text-xl text-[#5D2C8F]">Tip</h3>
            <p className="text-sm text-[#6E6074] mt-3 leading-relaxed">
              {isOwner ? (
                <>First time here? Head to <strong>Leads</strong> and click <em>Import from spreadsheet</em> to bring in every contact from your old Excel in one go.</>
              ) : (
                <>You'll see only the sections that have been granted to you in the sidebar. If something's missing, ask Jadesola to update your permissions on the Admin users page.</>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickLink({ to, label }) {
  return (
    <a href={to} className="px-4 py-3 rounded-lg bg-[#FAF6EE] border border-[#E5E0D8] text-[#5D2C8F] text-sm hover:bg-white">
      {label} →
    </a>
  );
}

function _fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Term-revenue comparison chip — shows the active term's revenue beside
 * the previous term's, with a colour-coded delta arrow. "Revenue" here
 * means the total agreed (invoice.total summed across paid+partial+sent)
 * not just what's cleared, so partial payers count fully.
 */
function TermRevenueChip({ stats }) {
  if (!stats) return null;
  const current = stats.current_term_revenue_gbp || 0;
  const previous = stats.previous_term_revenue_gbp || 0;
  const prevLabel = stats.previous_term_label;
  const delta = previous > 0 ? ((current - previous) / previous) * 100 : null;
  const Icon = delta == null ? Minus : delta >= 0 ? TrendingUp : TrendingDown;
  const colour = delta == null ? "#9A8DA6" : delta >= 0 ? "#348469" : "#C06B4E";
  return (
    <div className="admin-surface p-5 mt-6" data-testid="term-revenue-chip">
      <div className="flex flex-wrap items-center gap-5">
        <div>
          <div className="text-xs text-[#6E6074] uppercase tracking-widest">{stats.current_term || "This term"}</div>
          <div className="font-display text-2xl text-[#5D2C8F] mt-0.5">£{current.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</div>
        </div>
        {prevLabel && (
          <div className="flex items-center gap-3 pl-5 border-l border-[#E5DCE6]">
            <div>
              <div className="text-xs text-[#6E6074] uppercase tracking-widest">vs {prevLabel}</div>
              <div className="font-display text-lg text-[#6E6074] mt-0.5">£{previous.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</div>
            </div>
            {delta != null && (
              <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium" style={{ color: colour, background: `${colour}1A` }} data-testid="term-revenue-delta">
                <Icon size={14} /> {delta >= 0 ? "+" : ""}{delta.toFixed(0)}%
              </div>
            )}
          </div>
        )}
        {!prevLabel && (
          <div className="text-xs text-[#6E6074] pl-5 border-l border-[#E5DCE6] max-w-md leading-snug">No previous term to compare against yet — once you mark a new term active, this chip will show how it's tracking.</div>
        )}
      </div>
    </div>
  );
}

/**
 * UptimeRobot setup card — turns "wire up uptime monitoring" into a
 * 90-second copy-paste task. The /api/admin/debug/health endpoint is
 * already exposed (auth-bypassed for ping use); admins just need to
 * point a free UptimeRobot HTTP monitor at it.
 */
function UptimeRobotCard() {
  const [copied, setCopied] = useState(false);
  const healthUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/health`;
  const onCopy = () => {
    navigator.clipboard.writeText(healthUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="admin-surface p-5 mt-6" data-testid="uptime-robot-card">
      <div className="flex items-start gap-3">
        <Bell size={18} style={{ color: "#5D2C8F" }} className="mt-1" />
        <div className="flex-1">
          <div className="text-xs text-[#6E6074] uppercase tracking-widest">Uptime monitoring</div>
          <div className="font-display text-lg text-[#5D2C8F]">Get a text/email if the site goes down</div>
          <p className="text-sm text-[#6E6074] mt-1 leading-relaxed">
            Free monitoring via <strong>UptimeRobot</strong>. Set it up once — they'll ping the site every 5 minutes and alert you if it stops responding.
          </p>
          <ol className="mt-3 space-y-2 text-sm text-[#3A2A47]">
            <li>1. Open <a href="https://uptimerobot.com/signUp" target="_blank" rel="noreferrer" className="text-[#5D2C8F] underline inline-flex items-center gap-1">UptimeRobot.com <ExternalLink size={11} /></a> and create a free account.</li>
            <li>2. Click <strong>+ New Monitor</strong> → choose <strong>HTTP(s)</strong>.</li>
            <li>3. Paste this URL into the "URL (or IP)" field:
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 bg-[#FAF6EE] border border-[#E5E0D8] rounded px-2 py-1.5 text-xs text-[#5D2C8F] break-all" data-testid="uptime-health-url">{healthUrl}</code>
                <button onClick={onCopy} className="sac-btn-secondary !py-1.5 !px-3 text-xs whitespace-nowrap" data-testid="uptime-copy-url">
                  {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
              </div>
            </li>
            <li>4. Set <strong>Monitoring interval</strong> to <strong>5 minutes</strong>.</li>
            <li>5. Add your email + (optional) phone number under <strong>Alert Contacts</strong>, save.</li>
          </ol>
          <p className="text-xs text-[#6E6074] mt-3 leading-snug">
            Tip: UptimeRobot's free plan gives you 50 monitors and SMS via email-to-SMS. If you want push alerts to your phone, install their iOS/Android app and you'll get instant notifications too.
          </p>
        </div>
      </div>
    </div>
  );
}

function OutstandingCard({ rows }) {
  const top = rows.slice(0, 6);
  const totalOwed = rows.reduce((sum, r) => sum + (r.balance_due || 0), 0);
  return (
    <div className="admin-surface p-5 mt-6" data-testid="outstanding-payments-card">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <Clock size={16} style={{ color: "#C9A24E" }} />
          <div>
            <div className="text-xs text-[#6E6074] uppercase tracking-widest">Outstanding payments</div>
            <div className="font-display text-lg text-[#5D2C8F]">
              {rows.length === 0 ? "All invoices settled — nice." : `${rows.length} invoice${rows.length === 1 ? "" : "s"} awaiting payment · £${totalOwed.toFixed(0)}`}
            </div>
          </div>
        </div>
        {rows.length > top.length && (
          <a href="/admin/invoices" className="text-xs text-[#5D2C8F] underline" data-testid="outstanding-view-all">View all ({rows.length}) →</a>
        )}
      </div>
      {top.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="outstanding-table">
            <thead>
              <tr className="text-left text-[11px] text-[#6E6074] uppercase tracking-wide border-b border-[#E5DCE6]">
                <th className="py-2 pr-2">Student</th>
                <th className="py-2 pr-2">Invoice</th>
                <th className="py-2 pr-2">Next due</th>
                <th className="py-2 pr-2 text-right">Amount</th>
                <th className="py-2 pr-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {top.map((r) => (
                <tr key={r.id} className="border-b border-[#F1ECE3]" data-testid={`outstanding-row-${r.id}`}>
                  <td className="py-2 pr-2 text-[#3A2A47]">{r.student_name || "—"}</td>
                  <td className="py-2 pr-2 text-[#5D2C8F] font-mono text-xs">{r.invoice_number}</td>
                  <td className="py-2 pr-2 text-[#3A2A47]">
                    {r.next_due_date ? _fmtDate(r.next_due_date) : <span className="text-[#C06B4E]">Immediately</span>}
                    {r.next_due_label && <span className="text-[11px] text-[#6E6074] ml-1">· {r.next_due_label}</span>}
                  </td>
                  <td className="py-2 pr-2 text-right text-[#3A2A47]">£{(r.next_due_amount || 0).toFixed(0)}</td>
                  <td className="py-2 pr-2 text-right font-medium text-[#5D2C8F]">£{(r.balance_due || 0).toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const SERVICE_LABELS = {
  mongo: { label: "Database (MongoDB)", hint: "Where all your leads, students, invoices & images live" },
  smtp: { label: "Email (Gmail SMTP)", hint: "Welcome packs, invoices, receipts, enquiry notifications" },
  posthog: { label: "Web Analytics (PostHog)", hint: "Page views & visitor tracking" },
  meta: { label: "Instagram + Facebook", hint: "Scheduled social posts" },
  llm_key: { label: "AI Assistant (LLM key)", hint: "Auto blog drafts + social captions" },
};

function HealthCard({ health, loading, onRefresh }) {
  const services = health?.services || {};
  const entries = Object.entries(services);
  const allGreen = health?.ok;
  const failing = entries.filter(([, v]) => !v.ok);
  return (
    <div className="admin-surface p-5 mt-6" data-testid="system-health-card">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <Activity size={16} style={{ color: allGreen ? "#4FA07C" : "#C06B4E" }} />
          <div>
            <div className="text-xs text-[#6E6074] uppercase tracking-widest">System health</div>
            <div className="font-display text-lg text-[#5D2C8F]">
              {loading
                ? "Running checks…"
                : allGreen
                ? "All systems operational"
                : `${failing.length} service${failing.length > 1 ? "s" : ""} need${failing.length > 1 ? "" : "s"} attention`}
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="p-1.5 text-[#5D2C8F] rounded-md hover:bg-[#F4EDDD]"
          aria-label="Re-run health check"
          data-testid="system-health-refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {health?.error && <div className="text-sm text-[#C06B4E] mb-2">{health.error}</div>}

      {entries.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {entries.map(([key, s]) => {
            const meta = SERVICE_LABELS[key] || { label: key, hint: "" };
            const color = s.ok ? "#4FA07C" : "#C06B4E";
            return (
              <div key={key} className="p-3 rounded-lg bg-[#FAF6EE] border border-[#E5E0D8]" data-testid={`health-${key}`}>
                <div className="flex items-start gap-2">
                  {s.ok ? <CheckCircle2 size={14} style={{ color }} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} style={{ color }} className="mt-0.5 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#5D2C8F] truncate">{meta.label}</div>
                    <div className="text-[10px] text-[#8A7C92] uppercase tracking-wide mt-0.5">
                      {s.ok ? `OK · ${s.latency_ms}ms` : "Needs attention"}
                    </div>
                    <div className="text-xs text-[#6E6074] mt-1 truncate" title={s.detail}>{s.detail || meta.hint}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && failing.length > 0 && (
        <div className="mt-3 p-3 rounded-lg bg-[#FDF1EC] border border-[#F0C9B7] text-xs text-[#6B3520] leading-relaxed">
          <strong>How to fix:</strong> open <span className="font-mono">/app/backend/.env</span> and check the env var for the failing service (SMTP_*, POSTHOG_*, META_*). Once fixed, redeploy — the red chip will go green within seconds.
        </div>
      )}
    </div>
  );
}


function TermBreakdownRow({ term, can }) {
  const kind = term.kind || (term.is_active ? "current" : "previous");
  const badge = {
    previous: { label: "Previous", cls: "bg-[#E5E0D8] text-[#6E6074]" },
    current:  { label: "Current",  cls: "bg-[#8EC9C7]/25 text-[#5D2C8F]" },
    upcoming: { label: "Upcoming", cls: "bg-[#E8C37A]/25 text-[#8F6B1F]" },
  }[kind];
  const studentsHref = can("students") ? `/admin/students?term=${encodeURIComponent(term.label)}` : null;
  const invoicesHref = can("invoices") ? `/admin/invoices?term=${encodeURIComponent(term.label)}` : null;
  return (
    <div className={`rounded-lg p-3 border ${kind === "current" ? "border-[#5D2C8F]/40 bg-[#FAF6EE]" : kind === "upcoming" ? "border-[#E8C37A]/40 bg-white" : "border-[#E5E0D8] bg-white/60"}`} data-testid={`term-row-${term.label.replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-[#5D2C8F] truncate flex items-center gap-2">
            {term.label}
            {badge && <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>}
          </div>
          {term.start_date && <div className="text-[10px] text-[#6E6074] mt-0.5">Starts {new Date(term.start_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</div>}
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        {can("students") && (
          <Link to={studentsHref || "/admin/students"} className="flex items-center justify-between bg-white border border-[#E5E0D8] rounded px-2 py-1.5 hover:border-[#5D2C8F]">
            <span className="text-[#6E6074]"><Users size={11} className="inline mr-1" />Students</span>
            <span className="font-medium text-[#5D2C8F]">{term.students ?? 0}</span>
          </Link>
        )}
        {can("invoices") && (
          <Link to={invoicesHref || "/admin/invoices"} className="flex items-center justify-between bg-white border border-[#E5E0D8] rounded px-2 py-1.5 hover:border-[#5D2C8F]">
            <span className="text-[#6E6074]"><Receipt size={11} className="inline mr-1" />Paid</span>
            <span className="font-medium text-[#5D2C8F]">{term.invoices_paid ?? 0}<span className="text-[#A685BD]">/{term.invoices_total ?? 0}</span></span>
          </Link>
        )}
      </div>
    </div>
  );
}
